/**
 * ExportChatModal — Iter 153.43 (Feb 9, 2026, Yash).
 *
 * Lets the agent download a chat as CSV / TXT / PDF. Calls
 * /api/leads/{id}/export?format=... and triggers a browser download.
 */
import { useState } from "react";
import { FileSpreadsheet, FileText, FileType2, X, Loader2 } from "lucide-react";
import { leadsApi } from "../../lib/api";
import { toast } from "sonner";

export default function ExportChatModal({ lead, onClose }) {
  const [busy, setBusy] = useState(null);

  if (!lead) return null;

  const handleExport = async (fmt) => {
    setBusy(fmt);
    try {
      const res = await leadsApi.exportChat(lead.id, fmt);
      // Derive filename from headers if available, else fallback
      const cd = res.headers?.["content-disposition"] || res.headers?.["Content-Disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const filename = match ? match[1] : `chat_${lead.phone || lead.id}.${fmt}`;
      const blob = new Blob([res.data], {
        type: res.headers?.["content-type"] || "application/octet-stream",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Chat exported as ${fmt.toUpperCase()}`);
    } catch (err) {
      console.error("[export-chat] failed:", err);
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4 animate-fade-in" data-testid="export-chat-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-[--border]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[--border]">
          <div>
            <h2 className="text-[15px] font-bold text-[--text-primary]" style={{ fontFamily: "Outfit, sans-serif" }}>Export chat</h2>
            <p className="text-[11px] text-[--text-muted] mt-0.5">
              {lead.name || "(no name)"} · +{lead.phone}
            </p>
          </div>
          <button onClick={onClose} className="btn-icon" data-testid="export-close-btn"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-2">
          <button
            onClick={() => handleExport("pdf")}
            disabled={!!busy}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[--border] hover:border-red-300 hover:bg-red-50/40 transition-all text-left disabled:opacity-50"
            data-testid="export-pdf-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
              {busy === "pdf" ? <Loader2 size={18} className="animate-spin text-red-600" /> : <FileType2 size={18} className="text-red-600" />}
            </div>
            <div className="flex-1">
              <div className="font-bold text-[13px] text-[--text-primary]">PDF</div>
              <div className="text-[11px] text-[--text-muted]">Printable, formatted document</div>
            </div>
          </button>
          <button
            onClick={() => handleExport("csv")}
            disabled={!!busy}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[--border] hover:border-green-300 hover:bg-green-50/40 transition-all text-left disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
              {busy === "csv" ? <Loader2 size={18} className="animate-spin text-green-600" /> : <FileSpreadsheet size={18} className="text-green-600" />}
            </div>
            <div className="flex-1">
              <div className="font-bold text-[13px] text-[--text-primary]">CSV</div>
              <div className="text-[11px] text-[--text-muted]">Spreadsheet-friendly (Excel, Sheets)</div>
            </div>
          </button>
          <button
            onClick={() => handleExport("txt")}
            disabled={!!busy}
            className="w-full flex items-center gap-3 p-3 rounded-xl border border-[--border] hover:border-blue-300 hover:bg-blue-50/40 transition-all text-left disabled:opacity-50"
            data-testid="export-txt-btn"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              {busy === "txt" ? <Loader2 size={18} className="animate-spin text-blue-600" /> : <FileText size={18} className="text-blue-600" />}
            </div>
            <div className="flex-1">
              <div className="font-bold text-[13px] text-[--text-primary]">Plain text (.txt)</div>
              <div className="text-[11px] text-[--text-muted]">Simple, lightweight format</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
